import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { HStack, Modal, ModalOverlay, Text } from '@chakra-ui/react'
import { clsx } from 'clsx'
import dayjs from 'dayjs'
import parse from 'html-react-parser'
import { createPortal } from 'react-dom'

import { useDeleteJobApplicationMutation } from '@core/app/api/postsApi'
import { ApplyV2CustomFieldTypeConst } from '@core/app/types/applyV2Type'
import type { ApplyJobSettingsV2 } from '@core/app/types/postsApiType'
import ApplySkeleton from '@core/components/ApplyDialogV2/ApplySkeleton'
import ApplyV2Picture from '@core/components/ApplyDialogV2/ApplyV2Picture'
import ApplyV2Tooltip from '@core/components/ApplyDialogV2/ApplyV2Tooltip'
import { getStoredFilePreviewAuth, useFilePreviewSession } from '@core/components/FilePreview'
import TextEllipsis from '@core/components/TextEllipsis'
import { useFilePreview } from '@core/utils/contexts/FilePreviewContext/FilePreviewContext'
import decodeHtmlEntitiesRecursive from '@core/utils/helpers/decodeHtmlEntitiesRecursive'
import getCurrencyById from '@core/utils/helpers/getCurrencyById'
import useApplyV2NavTranslations from '@core/utils/hooks/useApplyV2NavTranslations'
import useFormattedDatesPeriod from '@core/utils/hooks/useFormattedDatesPeriod'

import PostPageV2File from '@page/post-v2/components/PostPageV2File'
import WithdrawApplicationModal from '@page/post-v2/components/WithdrawApplicationModal'
import { parseWithdrawError } from '@page/post-v2/components/WithdrawApplicationModal/parseWithdrawError'
import type { ApplicationDetailsV2, ApplicationFileV2, PostInfoV2 } from '@page/post-v2/types'
import './PostPageHeader2ApplyStatus.scss'

const baseClass = 'PostPageHeader2ApplyStatus'

interface ApplyStatusProps {
    postInfo?: PostInfoV2 | undefined | null
    isLoading?: boolean
    postApplySettingsData?: ApplyJobSettingsV2 | undefined | null
    applicationDetailsData?: ApplicationDetailsV2 | undefined | null
}

type StatusConfig = {
    variant: 'declined' | 'applied' | 'processed' | 'withdrawn'
    statusClass: 'declined' | 'applied' | 'processed' | 'withdrawn'
    statusLabel: string
    dateText: string
    hasWithdrawButton: boolean
    icon: React.ReactNode
    modalIcon: React.ReactNode
}

function PostPageHeader2ApplyStatus({
    isLoading,
    postInfo,
    postApplySettingsData,
    applicationDetailsData,
}: ApplyStatusProps) {
    const { getTitle } = useApplyV2NavTranslations()
    const { openPreview } = useFilePreview()
    const { openFilePreview } = useFilePreviewSession()
    const { formatDateRange } = useFormattedDatesPeriod()
    const postName = postInfo?.data?.jobPostingName
    const applicationStatusId = applicationDetailsData?.data?.application?.statusId
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(false)
    const previousBodyOverflow = useRef('')
    const modalBodyRef = useRef<HTMLDivElement | null>(null)
    const closeModal = useCallback(() => setIsModalOpen(false), [])
    const openModal = useCallback(() => setIsModalOpen(true), [])
    const [deleteApplication, { isLoading: isDeleteApplicationLoading, isSuccess }] = useDeleteJobApplicationMutation()
    const [isWithdrawn, setIsWithdrawn] = useState(false)
    const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false)
    const [withdrawReasonError, setWithdrawReasonError] = useState('')
    const [withdrawGeneralError, setWithdrawGeneralError] = useState('')
    const [isAnonModalOpen, setIsAnonModalOpen] = useState(false)

    const applicationStatus = postApplySettingsData?.data?.application?.status

    const applyDate = useMemo(() => {
        const months: Record<number, string> = {
            1: getTitle('489', 'Jan'),
            2: getTitle('490', 'Feb'),
            3: getTitle('491', 'Mar'),
            4: getTitle('492', 'Apr'),
            5: getTitle('493', 'May'),
            6: getTitle('494', 'Jun'),
            7: getTitle('495', 'Jul'),
            8: getTitle('496', 'Aug'),
            9: getTitle('497', 'Sep'),
            10: getTitle('498', 'Oct'),
            11: getTitle('499', 'Nov'),
            12: getTitle('500', 'Dec'),
        }

        const appliedAt = postApplySettingsData?.data?.application?.applied

        if (!appliedAt) {
            return ''
        }

        const applied = dayjs(appliedAt)
        const month = applied.month() + 1
        const day = applied.date()
        const year = applied.year()
        const formattedDate = `${day} ${months[month]} ${year}`

        if (applied.isSame(dayjs(), 'day')) {
            return `${getTitle('152', 'Today')}, ${formattedDate}`
        }

        if (applied.isSame(dayjs().subtract(1, 'day'), 'day')) {
            return `${getTitle('2839', 'Yesterday')}, ${formattedDate}`
        }

        return formattedDate
    }, [getTitle, postApplySettingsData?.data?.application?.applied])

    const resetWithdrawErrors = useCallback(() => {
        setWithdrawReasonError('')
        setWithdrawGeneralError('')
    }, [])

    const showWithdrawError = (payload: unknown) => {
        const parsed = parseWithdrawError(payload)
        setWithdrawReasonError(parsed?.reason ?? '')
        setWithdrawGeneralError(parsed?.isGeneral === false ? '' : getTitle('5858', 'Something went wrong'))
    }

    const deleteApplicationHandler = async (id: number | undefined | null, reason: string) => {
        if (!id) return
        resetWithdrawErrors()
        try {
            const response = await deleteApplication({
                id,
                reason,
            }).unwrap()

            if (parseWithdrawError(response)) {
                showWithdrawError(response)
                return
            }

            setIsWithdrawModalOpen(false)
            setIsWithdrawn(true)
            localStorage.removeItem('after_apply')
            localStorage.removeItem('after_apply_id')
            localStorage.removeItem('after_apply_title')
            localStorage.removeItem('applying_process')
            window.location.reload()
        } catch (error) {
            showWithdrawError(error ?? {})
        }
    }

    const statusMap = useMemo<Record<number, StatusConfig>>(
        () => ({
            0: {
                variant: 'declined',
                statusClass: 'declined',
                statusLabel: applicationStatus || getTitle('1579', 'Declined'),
                dateText: applyDate,
                hasWithdrawButton: false,
                icon: (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M5.31774 4.02976C6.60662 3.01764 8.23152 2.41406 9.9974 2.41406C14.1856 2.41406 17.5807 5.80924 17.5807 9.9974C17.5807 11.7633 16.9771 13.3882 15.965 14.677L5.31774 4.02976ZM4.23462 5.06795C3.09962 6.39357 2.41406 8.11542 2.41406 9.9974C2.41406 14.1856 5.80924 17.5807 9.9974 17.5807C11.8794 17.5807 13.6012 16.8952 14.9268 15.7602L4.23462 5.06795ZM9.9974 0.914062C4.98081 0.914062 0.914062 4.98081 0.914062 9.9974C0.914062 15.014 4.98081 19.0807 9.9974 19.0807C15.014 19.0807 19.0807 15.014 19.0807 9.9974C19.0807 4.98081 15.014 0.914062 9.9974 0.914062Z"
                            fill="#B62626"
                        />
                    </svg>
                ),
                modalIcon: (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M6.00928 3.89634C7.15585 3.14516 8.52691 2.70833 10 2.70833C14.0271 2.70833 17.2917 5.97292 17.2917 10C17.2917 11.4731 16.8548 12.8441 16.1037 13.9907L6.00928 3.89634ZM4.44411 5.27744C3.36158 6.54974 2.70833 8.19859 2.70833 10C2.70833 14.0271 5.97292 17.2917 10 17.2917C11.8014 17.2917 13.4503 16.6384 14.7226 15.5559L4.44411 5.27744ZM10 0.625C4.82233 0.625 0.625 4.82233 0.625 10C0.625 15.1777 4.82233 19.375 10 19.375C15.1777 19.375 19.375 15.1777 19.375 10C19.375 4.82233 15.1777 0.625 10 0.625Z"
                            fill="#B62626"
                        />
                    </svg>
                ),
            },
            1: {
                variant: 'applied',
                statusClass: 'applied',
                // statusLabel: applicationStatus || getTitle('1215', 'Applied'),
                statusLabel: getTitle('1215', 'Applied'),
                dateText: applyDate,
                hasWithdrawButton: true,
                icon: (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M18.0778 4.39941C18.408 4.71996 18.4159 5.24754 18.0954 5.57779L8.3895 15.5778C8.23256 15.7395 8.01684 15.8307 7.79151 15.8307C7.56619 15.8307 7.35046 15.7395 7.19353 15.5778L1.89941 10.1232C1.57887 9.79299 1.58674 9.26541 1.917 8.94487C2.24726 8.62432 2.77484 8.6322 3.09538 8.96245L7.79151 13.8009L16.8994 4.417C17.22 4.08674 17.7475 4.07887 18.0778 4.39941Z"
                            fill="#00853D"
                        />
                    </svg>
                ),
                modalIcon: (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M9.9974 0.914062C4.98081 0.914062 0.914062 4.98081 0.914062 9.9974C0.914062 15.014 4.98081 19.0807 9.9974 19.0807C15.014 19.0807 19.0807 15.014 19.0807 9.9974C19.0807 4.98081 15.014 0.914062 9.9974 0.914062ZM13.8518 8.4535C14.1497 8.16573 14.158 7.69093 13.8702 7.393C13.5824 7.09507 13.1076 7.08684 12.8097 7.37461L9.01702 11.038L7.18514 9.26855C6.88721 8.98078 6.41241 8.98901 6.12464 9.28694C5.83687 9.58487 5.8451 10.0597 6.14303 10.3474L8.49597 12.6202C8.78662 12.9009 9.24743 12.9009 9.53808 12.6202L13.8518 8.4535Z"
                            fill="#00853D"
                        />
                    </svg>
                ),
            },
            2: {
                variant: 'processed',
                statusClass: 'processed',
                // statusLabel: applicationStatus || getTitle('6826', 'Being processed'),
                statusLabel: getTitle('6826', 'Being processed'),
                dateText: applyDate,
                hasWithdrawButton: false,
                icon: (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M4.10542 5.16421C2.84549 3.90428 3.73783 1.75 5.51964 1.75H14.4841C16.2659 1.75 17.1582 3.90428 15.8983 5.16421L11.0625 10L15.8983 14.8358C17.1582 16.0957 16.2659 18.25 14.4841 18.25H5.51964C3.73783 18.25 2.84549 16.0957 4.10542 14.8358L8.94121 10L4.10542 5.16421ZM10.0019 8.93934L14.8377 4.10355C15.1526 3.78857 14.9296 3.25 14.4841 3.25H5.51964C5.07418 3.25 4.8511 3.78857 5.16608 4.10355L10.0019 8.93934ZM10.0019 11.0607L5.16608 15.8964C4.8511 16.2114 5.07418 16.75 5.51964 16.75H14.4841C14.9296 16.75 15.1526 16.2114 14.8377 15.8964L10.0019 11.0607Z"
                            fill="#245AF0"
                        />
                    </svg>
                ),
                modalIcon: (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M4.10542 5.16421C2.84549 3.90428 3.73783 1.75 5.51964 1.75H14.4841C16.2659 1.75 17.1582 3.90428 15.8983 5.16421L11.0625 10L15.8983 14.8358C17.1582 16.0957 16.2659 18.25 14.4841 18.25H5.51964C3.73783 18.25 2.84549 16.0957 4.10542 14.8358L8.94121 10L4.10542 5.16421ZM10.0019 8.93934L14.8377 4.10355C15.1526 3.78857 14.9296 3.25 14.4841 3.25H5.51964C5.07418 3.25 4.8511 3.78857 5.16608 4.10355L10.0019 8.93934ZM10.0019 11.0607L5.16608 15.8964C4.8511 16.2114 5.07418 16.75 5.51964 16.75H14.4841C14.9296 16.75 15.1526 16.2114 14.8377 15.8964L10.0019 11.0607Z"
                            fill="#245AF0"
                        />
                    </svg>
                ),
            },
            3: {
                variant: 'applied',
                statusClass: 'applied',
                statusLabel: applicationStatus || getTitle('43', 'Employed'),
                dateText: applyDate,
                hasWithdrawButton: false,
                icon: (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M14.6435 1.93775C14.6111 1.90617 14.5595 1.90617 14.5271 1.93775L12.1435 4.26324C11.8478 4.55176 11.451 4.71325 11.0378 4.71325H7.16865C7.12262 4.71325 7.08531 4.75056 7.08531 4.79658V9.73154C7.08531 10.3299 7.57034 10.8149 8.16865 10.8149C8.76695 10.8149 9.25198 10.3299 9.25198 9.73154V8.51505C9.25198 8.05652 9.6237 7.68479 10.0822 7.68479H15.6551L18.0335 5.36436C18.067 5.33166 18.067 5.27777 18.0335 5.24506L14.6435 1.93775ZM17.8031 7.68479L19.081 6.43803C19.7179 5.8167 19.7179 4.79273 19.081 4.17139L15.691 0.864075C15.076 0.264059 14.0947 0.264058 13.4796 0.864074L11.096 3.18957C11.0804 3.20475 11.0596 3.21325 11.0378 3.21325H7.16865C6.2942 3.21325 5.58531 3.92213 5.58531 4.79658V5.43577L2.3963 8.547C2.09087 8.84498 1.91865 9.25362 1.91865 9.68032V12.5551C1.91865 12.5776 1.90958 12.5991 1.89351 12.6148L0.922966 13.5616C0.286094 14.183 0.286095 15.2069 0.922967 15.8283L4.31297 19.1356C4.92798 19.7356 5.90931 19.7356 6.52433 19.1356L9.36629 16.363C9.38185 16.3478 9.40273 16.3393 9.42448 16.3393H12.9085C14.2078 16.3393 15.2686 15.3182 15.3323 14.0346C16.2094 13.8202 16.8983 13.1259 17.1049 12.2457C18.1934 11.9829 19.002 11.0026 19.002 9.83339V8.60869C19.002 8.09844 18.5883 7.68479 18.0781 7.68479H17.8031ZM5.58531 7.53138L3.44379 9.62068C3.42771 9.63636 3.41865 9.65787 3.41865 9.68032V12.5551C3.41865 12.9818 3.24642 13.3905 2.94099 13.6884L1.97045 14.6353C1.93693 14.668 1.93693 14.7219 1.97045 14.7546L5.36045 18.0619C5.39282 18.0935 5.44447 18.0935 5.47684 18.0619L8.3188 15.2893C8.61453 15.0008 9.01132 14.8393 9.42448 14.8393H12.9085C13.4204 14.8393 13.8353 14.4243 13.8353 13.9124V13.4844C13.8353 12.9979 14.2297 12.6035 14.7163 12.6035H14.7578C15.2608 12.6035 15.6686 12.1957 15.6686 11.6926V11.6667C15.6686 11.1963 16.05 10.8149 16.5205 10.8149C17.0626 10.8149 17.502 10.3755 17.502 9.83339V9.1848H10.752V9.73154C10.752 11.1583 9.59538 12.3149 8.16865 12.3149C6.74191 12.3149 5.58531 11.1583 5.58531 9.73154V7.53138Z"
                            fill="#00853D"
                        />
                    </svg>
                ),
                modalIcon: (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M14.6435 1.93775C14.6111 1.90617 14.5595 1.90617 14.5271 1.93775L12.1435 4.26324C11.8478 4.55176 11.451 4.71325 11.0378 4.71325H7.16865C7.12262 4.71325 7.08531 4.75056 7.08531 4.79658V9.73154C7.08531 10.3299 7.57034 10.8149 8.16865 10.8149C8.76695 10.8149 9.25198 10.3299 9.25198 9.73154V8.51505C9.25198 8.05652 9.6237 7.68479 10.0822 7.68479H15.6551L18.0335 5.36436C18.067 5.33166 18.067 5.27777 18.0335 5.24506L14.6435 1.93775ZM17.8031 7.68479L19.081 6.43803C19.7179 5.8167 19.7179 4.79273 19.081 4.17139L15.691 0.864075C15.076 0.264059 14.0947 0.264058 13.4796 0.864074L11.096 3.18957C11.0804 3.20475 11.0596 3.21325 11.0378 3.21325H7.16865C6.2942 3.21325 5.58531 3.92213 5.58531 4.79658V5.43577L2.3963 8.547C2.09087 8.84498 1.91865 9.25362 1.91865 9.68032V12.5551C1.91865 12.5776 1.90958 12.5991 1.89351 12.6148L0.922966 13.5616C0.286094 14.183 0.286095 15.2069 0.922967 15.8283L4.31297 19.1356C4.92798 19.7356 5.90931 19.7356 6.52433 19.1356L9.36629 16.363C9.38185 16.3478 9.40273 16.3393 9.42448 16.3393H12.9085C14.2078 16.3393 15.2686 15.3182 15.3323 14.0346C16.2094 13.8202 16.8983 13.1259 17.1049 12.2457C18.1934 11.9829 19.002 11.0026 19.002 9.83339V8.60869C19.002 8.09844 18.5883 7.68479 18.0781 7.68479H17.8031ZM5.58531 7.53138L3.44379 9.62068C3.42771 9.63636 3.41865 9.65787 3.41865 9.68032V12.5551C3.41865 12.9818 3.24642 13.3905 2.94099 13.6884L1.97045 14.6353C1.93693 14.668 1.93693 14.7219 1.97045 14.7546L5.36045 18.0619C5.39282 18.0935 5.44447 18.0935 5.47684 18.0619L8.3188 15.2893C8.61453 15.0008 9.01132 14.8393 9.42448 14.8393H12.9085C13.4204 14.8393 13.8353 14.4243 13.8353 13.9124V13.4844C13.8353 12.9979 14.2297 12.6035 14.7163 12.6035H14.7578C15.2608 12.6035 15.6686 12.1957 15.6686 11.6926V11.6667C15.6686 11.1963 16.05 10.8149 16.5205 10.8149C17.0626 10.8149 17.502 10.3755 17.502 9.83339V9.1848H10.752V9.73154C10.752 11.1583 9.59538 12.3149 8.16865 12.3149C6.74191 12.3149 5.58531 11.1583 5.58531 9.73154V7.53138Z"
                            fill="#00853D"
                        />
                    </svg>
                ),
            },
            4: {
                variant: 'withdrawn',
                statusClass: 'withdrawn',
                statusLabel: applicationStatus || getTitle('10181', 'Withdrawn'),
                dateText: applyDate,
                hasWithdrawButton: false,
                icon: (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M7.00795 2.37296C7.3068 2.65977 7.31655 3.13455 7.02973 3.43339L3.42341 7.19095H13.7608C16.6989 7.19095 19.0807 9.57274 19.0807 12.5108C19.0807 15.4489 16.6989 17.8307 13.7608 17.8307H12.1904C11.7762 17.8307 11.4404 17.4949 11.4404 17.0807C11.4404 16.6665 11.7762 16.3307 12.1904 16.3307H13.7608C15.8705 16.3307 17.5807 14.6205 17.5807 12.5108C17.5807 10.4012 15.8705 8.69095 13.7608 8.69095H3.42341L7.02973 12.4485C7.31655 12.7473 7.3068 13.2221 7.00795 13.5089C6.70911 13.7958 6.23433 13.786 5.94752 13.4872L1.12296 8.46027C0.844432 8.17007 0.844432 7.71182 1.12296 7.42162L5.94752 2.39473C6.23433 2.09589 6.70911 2.08614 7.00795 2.37296Z"
                            fill="#58585B"
                        />
                    </svg>
                ),
                modalIcon: (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M7.00795 2.37296C7.3068 2.65977 7.31655 3.13455 7.02973 3.43339L3.42341 7.19095H13.7608C16.6989 7.19095 19.0807 9.57274 19.0807 12.5108C19.0807 15.4489 16.6989 17.8307 13.7608 17.8307H12.1904C11.7762 17.8307 11.4404 17.4949 11.4404 17.0807C11.4404 16.6665 11.7762 16.3307 12.1904 16.3307H13.7608C15.8705 16.3307 17.5807 14.6205 17.5807 12.5108C17.5807 10.4012 15.8705 8.69095 13.7608 8.69095H3.42341L7.02973 12.4485C7.31655 12.7473 7.3068 13.2221 7.00795 13.5089C6.70911 13.7958 6.23433 13.786 5.94752 13.4872L1.12296 8.46027C0.844432 8.17007 0.844432 7.71182 1.12296 7.42162L5.94752 2.39473C6.23433 2.09589 6.70911 2.08614 7.00795 2.37296Z"
                            fill="#19191A"
                        />
                    </svg>
                ),
            },
        }),
        [applyDate, applicationStatus, getTitle]
    )

    const status = typeof applicationStatusId === 'number' ? statusMap[applicationStatusId] : undefined
    const title = decodeHtmlEntitiesRecursive(postName)
    const { cv, attachments } = applicationDetailsData?.data?.files || {
        cv: null,
        attachments: [],
    }
    const { questions, anonReason } = applicationDetailsData?.data || { questions: [] }
    const { applicationText } = applicationDetailsData?.data?.application || {}

    useEffect(() => {
        if (!isModalOpen || typeof document === 'undefined') {
            return
        }

        previousBodyOverflow.current = document.body.style.overflow
        document.body.style.overflow = 'hidden'

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                closeModal()
            }
        }

        document.addEventListener('keydown', handleKeyDown)

        return () => {
            document.body.style.overflow = previousBodyOverflow.current
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [closeModal, isModalOpen])

    useEffect(() => {
        if ((isSuccess || isWithdrawn) && typeof window !== 'undefined') {
            window.location.reload()
        }
    }, [isSuccess, isWithdrawn])

    useEffect(() => {
        if (!isModalOpen) {
            setIsHeaderCollapsed(false)
            return
        }

        const el = modalBodyRef.current
        if (!el) return

        const updateHeaderState = () => {
            setIsHeaderCollapsed(el.scrollTop > 24)
        }

        updateHeaderState()
        el.addEventListener('scroll', updateHeaderState)

        return () => el.removeEventListener('scroll', updateHeaderState)
    }, [isModalOpen])

    const renderQuestionItem = (question: (typeof questions)[number]) => {
        const questionName = decodeHtmlEntitiesRecursive(question.payload?.questionName)
        const questionType = question.payload?.questionType
        const questionValue = question.payload?.value

        switch (questionType) {
            case 'text':
            case 'longText':
            case 'number':
                return (
                    <div key={question.id} className={`${baseClass}__modal__body__questions__list__item`}>
                        <div className={`${baseClass}__modal__body__questions__list__item--question`}>
                            {questionName}
                        </div>
                        <div className={`${baseClass}__modal__body__questions__list__item--answer`}>
                            {questionValue}
                        </div>
                    </div>
                )
            case 'starRating':
                return (
                    <div key={question.id} className={`${baseClass}__modal__body__questions__list__item`}>
                        <div className={`${baseClass}__modal__body__questions__list__item--question`}>
                            {questionName}
                        </div>
                        <div className={`${baseClass}__modal__body__questions__list__item--answer`}>
                            <span className={`${baseClass}__modal__body__questions__list__item--answer__rating`}>
                                <span
                                    className={`${baseClass}__modal__body__questions__list__item--answer__rating__star`}
                                >
                                    <svg
                                        width="20"
                                        height="20"
                                        viewBox="0 0 20 20"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M11.05 1.8176C10.6199 0.949471 9.3801 0.949471 8.95003 1.8176L6.77815 6.20209L1.92047 6.90532C0.962761 7.04398 0.572374 8.22241 1.27187 8.90174L4.78527 12.3138L3.95598 17.1309C3.79046 18.0924 4.80071 18.8123 5.65465 18.365L10 16.0889L14.3455 18.365C15.1995 18.8125 16.2094 18.0921 16.044 17.1309L15.2147 12.3138L18.728 8.9018C19.4278 8.22236 19.037 7.04397 18.0795 6.90532L13.2219 6.20208L11.05 1.8176Z"
                                            fill="#FFBE33"
                                        />
                                    </svg>
                                </span>
                                {questionValue}
                            </span>
                        </div>
                    </div>
                )
            case 'attachment':
                return (
                    <div key={question.id} className={`${baseClass}__modal__body__questions__list__item`}>
                        <div className={`${baseClass}__modal__body__questions__list__item--question`}>
                            {questionName}
                        </div>
                        <div className={`${baseClass}__modal__body__questions__list__item--answer`}>
                            <div className={`${baseClass}__modal__body__questions__list__item--answer__files`}>
                                {Array.isArray(questionValue) &&
                                    questionValue.map((file) => {
                                        return (
                                            <ApplyV2Tooltip
                                                key={file.id}
                                                label={file.name}
                                                display={'block'}
                                                placement={'top'}
                                            >
                                                <PostPageV2File
                                                    key={file.id}
                                                    file={file}
                                                    onClick={(e) => {
                                                        e.preventDefault()
                                                        const previewAuth = getStoredFilePreviewAuth(
                                                            file.fileId,
                                                            'ad-question-file',
                                                            { extension: file.ext }
                                                        )
                                                        if (previewAuth) {
                                                            openFilePreview(previewAuth)
                                                        } else {
                                                            openPreview(
                                                                {
                                                                    Filename: file.name,
                                                                    Ext: file.ext,
                                                                    CandidateFileID: file.fileId,
                                                                    Size: file.size,
                                                                    uploaded: file.uploaded,

                                                                    // downloadUrl: file.downloadUrl,
                                                                    ...file,
                                                                },
                                                                'ad-question-file'
                                                            )
                                                        }
                                                    }}
                                                />
                                            </ApplyV2Tooltip>
                                        )
                                    })}
                            </div>
                        </div>
                    </div>
                )
            case 'yesNo':
            case 'qualifying':
                const noRes = questionValue === false && getTitle('129', 'No')
                const yesRes = questionValue && getTitle('128', 'Yes')

                return (
                    <div key={question.id} className={`${baseClass}__modal__body__questions__list__item`}>
                        <div className={`${baseClass}__modal__body__questions__list__item--question`}>
                            {questionName}
                        </div>
                        <div className={`${baseClass}__modal__body__questions__list__item--answer`}>
                            {noRes || yesRes}
                        </div>
                    </div>
                )
            case 'date':
                return (
                    <div key={question.id} className={`${baseClass}__modal__body__questions__list__item`}>
                        <div className={`${baseClass}__modal__body__questions__list__item--question`}>
                            {questionName}
                        </div>
                        <div className={`${baseClass}__modal__body__questions__list__item--answer`}>
                            {formatDateRange(questionValue, null, {
                                isShowPresent: false,
                                dateFormat: 'D MMM YYYY',
                            })}
                            вв
                        </div>
                    </div>
                )
            case 'dateRange':
                if (!Array.isArray(questionValue)) {
                    return
                }
                return (
                    <div key={question.id} className={`${baseClass}__modal__body__questions__list__item`}>
                        <div className={`${baseClass}__modal__body__questions__list__item--question`}>
                            {questionName}
                        </div>
                        <div className={`${baseClass}__modal__body__questions__list__item--answer`}>
                            {formatDateRange(questionValue[0], questionValue[1], {
                                isShowPresent: false,
                                dateFormat: 'D MMM YYYY',
                            })}
                        </div>
                    </div>
                )
            case 'singleSelect':
            case 'multiSelect':
                const arr: { id: number; name: string }[] = Object.values(questionValue || {})
                return (
                    <div key={question.id} className={`${baseClass}__modal__body__questions__list__item`}>
                        <div className={`${baseClass}__modal__body__questions__list__item--question`}>
                            {questionName}
                        </div>
                        <div className={`${baseClass}__modal__body__questions__list__item--answer`}>
                            {arr.map(({ name, id }, idx) => {
                                if (idx === arr.length - 1) {
                                    return <span key={id}>{name}</span>
                                }
                                return <span key={id}>{name}, </span>
                            })}
                        </div>
                    </div>
                )
            case 'customField':
                switch (question?.payload?.customFieldType) {
                    case ApplyV2CustomFieldTypeConst.SingleSelect:
                        if (questionValue) {
                            return (
                                <div key={question.id} className={`${baseClass}__modal__body__questions__list__item`}>
                                    <div className={`${baseClass}__modal__body__questions__list__item--question`}>
                                        {questionName}
                                    </div>
                                    <div className={`${baseClass}__modal__body__questions__list__item--answer`}>
                                        {questionValue?.name}
                                    </div>
                                </div>
                            )
                        }
                        return
                    case ApplyV2CustomFieldTypeConst.MultiSelect:
                        const arr: { id: number; name: string }[] = Object.values(questionValue || {})
                        return (
                            <div key={question.id} className={`${baseClass}__modal__body__questions__list__item`}>
                                <div className={`${baseClass}__modal__body__questions__list__item--question`}>
                                    {questionName}
                                </div>
                                <div className={`${baseClass}__modal__body__questions__list__item--answer`}>
                                    {arr.map(({ name, id }, idx) => {
                                        if (idx === arr.length - 1) {
                                            return <span key={id}>{name}</span>
                                        }
                                        return <span key={id}>{name}, </span>
                                    })}
                                </div>
                            </div>
                        )
                    case ApplyV2CustomFieldTypeConst.Text:
                        return (
                            <div key={question.id} className={`${baseClass}__modal__body__questions__list__item`}>
                                <div className={`${baseClass}__modal__body__questions__list__item--question`}>
                                    {questionName}
                                </div>
                                <div className={`${baseClass}__modal__body__questions__list__item--answer`}>
                                    {questionValue}
                                </div>
                            </div>
                        )
                    case ApplyV2CustomFieldTypeConst.Date:
                        return (
                            <div key={question.id} className={`${baseClass}__modal__body__questions__list__item`}>
                                <div className={`${baseClass}__modal__body__questions__list__item--question`}>
                                    {questionName}
                                </div>
                                <div className={`${baseClass}__modal__body__questions__list__item--answer`}>
                                    {formatDateRange(questionValue, null, {
                                        isShowPresent: false,
                                        dateFormat: 'D MMM YYYY',
                                    })}
                                </div>
                            </div>
                        )
                    case ApplyV2CustomFieldTypeConst.Integer:
                        if (typeof questionValue === 'number') {
                            const decimals = question.payload.properties?.decimals ?? 0
                            const flag = getCurrencyById(question.payload?.properties?.currencyId)
                            if (flag && questionValue) {
                                return (
                                    <div
                                        key={question.id}
                                        className={`${baseClass}__modal__body__questions__list__item`}
                                    >
                                        <div className={`${baseClass}__modal__body__questions__list__item--question`}>
                                            {questionName}
                                        </div>
                                        <div
                                            className={`${baseClass}__modal__body__questions__list__item--answer`}
                                            style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                                        >
                                            <ApplyV2Picture
                                                className={`${baseClass}__modal__body__questions__list__item--answer__currency`}
                                                fallbackSrc={`${window.location.protocol}//${window.location.hostname}/${flag?.img}`}
                                                alt=""
                                            />
                                            <span>{questionValue.toFixed(decimals)}</span>
                                        </div>
                                    </div>
                                )
                            }
                            return (
                                <div key={question.id} className={`${baseClass}__modal__body__questions__list__item`}>
                                    <div className={`${baseClass}__modal__body__questions__list__item--question`}>
                                        {questionName}
                                    </div>
                                    <div className={`${baseClass}__modal__body__questions__list__item--answer`}>
                                        <span>{questionValue.toFixed(decimals)}</span>
                                    </div>
                                </div>
                            )
                        }
                        return

                    default:
                        return
                }
            case 'attribute':
                return (
                    <div key={question.id} className={`${baseClass}__modal__body__questions__list__item`}>
                        <div className={`${baseClass}__modal__body__questions__list__item--question`}>
                            {questionName}
                        </div>
                        {typeof questionValue?.bool === 'boolean' && (
                            <div className={`${baseClass}__modal__body__questions__list__item--answer`}>
                                {questionValue?.bool ? getTitle('128', 'Yes') : getTitle('129', 'No')}
                            </div>
                        )}
                        {questionValue?.rating && (
                            <div className={`${baseClass}__modal__body__questions__list__item--answer`}>
                                <span className={`${baseClass}__modal__body__questions__list__item--answer__rating`}>
                                    <span
                                        className={`${baseClass}__modal__body__questions__list__item--answer__rating__star`}
                                    >
                                        <svg
                                            width="20"
                                            height="20"
                                            viewBox="0 0 20 20"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M11.05 1.8176C10.6199 0.949471 9.3801 0.949471 8.95003 1.8176L6.77815 6.20209L1.92047 6.90532C0.962761 7.04398 0.572374 8.22241 1.27187 8.90174L4.78527 12.3138L3.95598 17.1309C3.79046 18.0924 4.80071 18.8123 5.65465 18.365L10 16.0889L14.3455 18.365C15.1995 18.8125 16.2094 18.0921 16.044 17.1309L15.2147 12.3138L18.728 8.9018C19.4278 8.22236 19.037 7.04397 18.0795 6.90532L13.2219 6.20208L11.05 1.8176Z"
                                                fill="#FFBE33"
                                            />
                                        </svg>
                                    </span>
                                    {questionValue?.rating}
                                </span>
                            </div>
                        )}
                        {questionValue?.dropdown?.name && (
                            <div className={`${baseClass}__modal__body__questions__list__item--answer`}>
                                {questionValue?.dropdown?.name}
                            </div>
                        )}
                        {questionValue?.checkbox && Object.values(questionValue?.checkbox ?? {}).length > 0 && (
                            <div className={`${baseClass}__modal__body__questions__list__item--answer`}>
                                {(
                                    Object.values(questionValue?.checkbox ?? {}) as Array<{
                                        id: number
                                        name: string
                                        rating: number
                                    }>
                                ).map(({ id, name, rating }) => {
                                    return (
                                        <div key={id} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <span>{name}</span>
                                            {rating && rating > 0 ? (
                                                <span
                                                    className={`${baseClass}__modal__body__questions__list__item--answer__rating`}
                                                >
                                                    <span
                                                        className={`${baseClass}__modal__body__questions__list__item--answer__rating__star`}
                                                    >
                                                        <svg
                                                            width="20"
                                                            height="20"
                                                            viewBox="0 0 20 20"
                                                            fill="none"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <path
                                                                fillRule="evenodd"
                                                                clipRule="evenodd"
                                                                d="M11.05 1.8176C10.6199 0.949471 9.3801 0.949471 8.95003 1.8176L6.77815 6.20209L1.92047 6.90532C0.962761 7.04398 0.572374 8.22241 1.27187 8.90174L4.78527 12.3138L3.95598 17.1309C3.79046 18.0924 4.80071 18.8123 5.65465 18.365L10 16.0889L14.3455 18.365C15.1995 18.8125 16.2094 18.0921 16.044 17.1309L15.2147 12.3138L18.728 8.9018C19.4278 8.22236 19.037 7.04397 18.0795 6.90532L13.2219 6.20208L11.05 1.8176Z"
                                                                fill="#FFBE33"
                                                            />
                                                        </svg>
                                                    </span>
                                                    <span>{rating}</span>
                                                </span>
                                            ) : (
                                                <span></span>
                                            )}
                                        </div>
                                    )
                                })}
                            </div>
                        )}
                        {questionValue?.shortText && (
                            <div className={`${baseClass}__modal__body__questions__list__item--answer`}>
                                {questionValue?.shortText}
                            </div>
                        )}
                        {questionValue?.text && (
                            <div className={`${baseClass}__modal__body__questions__list__item--answer`}>
                                {questionValue?.text}
                            </div>
                        )}
                        {questionValue?.date && (
                            <div className={`${baseClass}__modal__body__questions__list__item--answer`}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    {formatDateRange(questionValue?.date, null, {
                                        isShowPresent: false,
                                        dateFormat: 'D MMMM YYYY',
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                )
            default:
                return (
                    <div key={question.id} className={`${baseClass}__modal__body__questions__list__item`}>
                        <div className={`${baseClass}__modal__body__questions__list__item--question`}>
                            {questionName}
                        </div>
                        {/*<div className={`${baseClass}__modal__body__questions__list__item--answer`}>*/}
                        {/*    {questionValue}*/}
                        {/*</div>*/}
                    </div>
                )
        }
    }

    if (isLoading) {
        return null
    }

    const modal =
        isModalOpen && status && typeof document !== 'undefined'
            ? createPortal(
                  <div className={`${baseClass}__overlay`} role="presentation">
                      <button
                          type="button"
                          className={`${baseClass}__overlay-backdrop`}
                          onClick={closeModal}
                          aria-label={getTitle('181', 'Close')}
                      />
                      <div
                          className={clsx(`${baseClass}__modal`, {
                              [`${baseClass}__modal--collapsed`]: isHeaderCollapsed,
                          })}
                          role="dialog"
                          aria-modal="true"
                          aria-labelledby="post-apply-status-modal-title"
                      >
                          <div className={`${baseClass}__modal__header`}>
                              <div className={`${baseClass}__modal__header__top`}>
                                  <div
                                      className={`${baseClass}__modal__header__top__icon ${baseClass}__modal__header__top__icon--${status.statusClass}`}
                                  >
                                      {status.modalIcon}
                                  </div>
                                  <TextEllipsis
                                      className={clsx(`${baseClass}__modal__header__top__title`, {
                                          [`${baseClass}__modal__header__top__title--collapsed`]: isHeaderCollapsed,
                                      })}
                                  >
                                      {title}
                                  </TextEllipsis>
                                  <ApplyV2Tooltip label={getTitle('181', 'Close')} placement="top">
                                      <button
                                          type="button"
                                          className={`${baseClass}__modal__close`}
                                          onClick={closeModal}
                                          aria-label={getTitle('181', 'Close')}
                                      >
                                          <svg
                                              width="16"
                                              height="16"
                                              viewBox="0 0 16 16"
                                              fill="none"
                                              xmlns="http://www.w3.org/2000/svg"
                                          >
                                              <path
                                                  fillRule="evenodd"
                                                  clipRule="evenodd"
                                                  d="M13.6239 2.19526C13.8843 2.45561 13.8843 2.87772 13.6239 3.13807L8.8524 7.90963L13.8047 12.8619C14.0651 13.1223 14.0651 13.5444 13.8047 13.8047C13.5444 14.0651 13.1223 14.0651 12.8619 13.8047L7.90959 8.85243L3.13807 13.6239C2.87772 13.8843 2.45561 13.8843 2.19526 13.6239C1.93491 13.3636 1.93491 12.9415 2.19526 12.6811L6.96678 7.90963L2.37605 3.31886C2.1157 3.05851 2.11571 2.6364 2.37606 2.37605C2.63641 2.11571 3.05852 2.11571 3.31887 2.37606L7.90959 6.96682L12.6811 2.19526C12.9415 1.93491 13.3636 1.93491 13.6239 2.19526Z"
                                                  fill="#77777C"
                                              />
                                          </svg>
                                      </button>
                                  </ApplyV2Tooltip>
                              </div>
                          </div>
                          <div
                              ref={modalBodyRef}
                              className={`${baseClass}__modal__body`}
                              onScroll={() => setIsHeaderCollapsed((modalBodyRef.current?.scrollTop ?? 0) > 24)}
                          >
                              <div className={`${baseClass}__modal__header__info`}>
                                  <div className={`${baseClass}__modal__header__info--row`}>
                                      <div className={`${baseClass}__modal__header__info--row--${status.statusClass}`}>
                                          {status.statusLabel}
                                      </div>
                                      <div className={`${baseClass}__modal__header__info--row--dot`}></div>
                                      <div className={`${baseClass}__modal__header__info--row--date`}>
                                          {status.dateText || '—'}
                                      </div>
                                      {anonReason && (
                                          <>
                                              <div className={`${baseClass}__modal__header__info--row--dot`}></div>
                                              <div
                                                  role={'button'}
                                                  className={`${baseClass}__modal__header__info--row--reason`}
                                                  onClick={() => setIsAnonModalOpen(true)}
                                              >
                                                  <svg
                                                      width="12"
                                                      height="12"
                                                      viewBox="0 0 12 12"
                                                      fill="none"
                                                      xmlns="http://www.w3.org/2000/svg"
                                                  >
                                                      <path
                                                          fillRule="evenodd"
                                                          clipRule="evenodd"
                                                          d="M9.51728 1.68023C9.44908 1.10056 8.90467 0.699541 8.33084 0.8063L6.01678 1.23682C6.00362 1.23927 5.99013 1.23927 5.97697 1.23682L3.66291 0.8063C3.08908 0.699542 2.54467 1.10056 2.47647 1.68023L2.03421 5.43943H1.24687C0.998347 5.43943 0.796875 5.6409 0.796875 5.88943C0.796875 6.13795 0.998347 6.33943 1.24687 6.33943H2.43438H3.62188H5.99687H8.37187H9.55937H10.7469C10.9954 6.33943 11.1969 6.13795 11.1969 5.88943C11.1969 5.6409 10.9954 5.43943 10.7469 5.43943H9.95954L9.51728 1.68023ZM3.20264 7.05661C2.02823 7.05661 1.07617 8.00867 1.07617 9.18308C1.07617 10.3575 2.02823 11.3096 3.20264 11.3096C4.3195 11.3096 5.23526 10.4485 5.32234 9.35407H6.67133C6.75841 10.4485 7.67416 11.3096 8.79102 11.3096C9.96544 11.3096 10.9175 10.3575 10.9175 9.18308C10.9175 8.00867 9.96544 7.05661 8.79102 7.05661C7.8727 7.05661 7.09034 7.63872 6.7928 8.45407H5.20086C4.90332 7.63872 4.12096 7.05661 3.20264 7.05661Z"
                                                          fill="#19191A"
                                                      />
                                                  </svg>
                                                  <ApplyV2Tooltip
                                                      label={decodeHtmlEntitiesRecursive(anonReason)}
                                                      placement={'top'}
                                                      maxWidth={'320px'}
                                                  >
                                                      <div
                                                          role={'button'}
                                                          className={`${baseClass}__modal__header__info--row--reason--text`}
                                                          onClick={() => setIsAnonModalOpen(true)}
                                                      >
                                                          {getTitle('1546', 'Anonymous')}
                                                      </div>
                                                  </ApplyV2Tooltip>
                                              </div>
                                          </>
                                      )}
                                  </div>
                                  <div className={`${baseClass}__modal__header__info--title`}>{title}</div>
                              </div>
                              {cv && (
                                  <div className={`${baseClass}__modal__body__cv`}>
                                      <div className={`${baseClass}__modal__body__cv__title`}>
                                          {getTitle('12990', 'Your CV')}
                                      </div>
                                      <div className={`${baseClass}__modal__body__cv__file`}>
                                          <ApplyV2Tooltip label={cv.filename} display={'block'} placement={'top-start'}>
                                              <PostPageV2File
                                                  file={cv}
                                                  onClick={(e) => {
                                                      e.preventDefault()
                                                      const previewAuth = getStoredFilePreviewAuth(cv.id, 'candidate', {
                                                          extension: cv.ext,
                                                      })
                                                      if (previewAuth) {
                                                          openFilePreview(previewAuth)
                                                      } else {
                                                          openPreview(
                                                              {
                                                                  Filename: cv.filename,
                                                                  Ext: cv.ext,
                                                                  CandidateFileID: cv.id,
                                                                  Size: cv.size,
                                                                  downloadUrl: cv.downloadUrl,
                                                                  ...cv,
                                                              },
                                                              'candidate'
                                                          )
                                                      }
                                                  }}
                                              />
                                          </ApplyV2Tooltip>
                                      </div>
                                  </div>
                              )}
                              {applicationText && (
                                  <div className={`${baseClass}__modal__body__applicationText`}>
                                      <div className={`${baseClass}__modal__body__applicationText__title`}>
                                          {getTitle('1221', 'Application text')}
                                      </div>
                                      <div className={`${baseClass}__modal__body__applicationText__content`}>
                                          {parse(decodeHtmlEntitiesRecursive(applicationText))}
                                      </div>
                                  </div>
                              )}
                              {attachments && attachments.length > 0 && (
                                  <div className={`${baseClass}__modal__body__attachments`}>
                                      <div className={`${baseClass}__modal__body__attachments__title`}>
                                          {getTitle('1044', 'Attachments')}
                                      </div>
                                      <div className={`${baseClass}__modal__body__attachments__files`}>
                                          {attachments.map((attachment: ApplicationFileV2) => (
                                              <ApplyV2Tooltip
                                                  key={attachment.id}
                                                  label={attachment.filename}
                                                  display={'block'}
                                                  placement={'top'}
                                              >
                                                  <PostPageV2File
                                                      key={attachment.id}
                                                      file={attachment}
                                                      onClick={(e) => {
                                                          e.preventDefault()
                                                          const previewAuth = getStoredFilePreviewAuth(
                                                              attachment.id,
                                                              'candidate',
                                                              { extension: attachment.ext }
                                                          )
                                                          if (previewAuth) {
                                                              openFilePreview(previewAuth)
                                                          } else {
                                                              openPreview(
                                                                  {
                                                                      Filename: attachment.filename,
                                                                      Ext: attachment.ext,
                                                                      CandidateFileID: attachment.id,
                                                                      Size: attachment.size,
                                                                      downloadUrl: attachment.downloadUrl,
                                                                      ...attachment,
                                                                  },
                                                                  'candidate'
                                                              )
                                                          }
                                                      }}
                                                  />
                                              </ApplyV2Tooltip>
                                          ))}
                                      </div>
                                  </div>
                              )}
                              {questions && questions.length > 0 && (
                                  <div className={`${baseClass}__modal__body__questions`}>
                                      <div className={`${baseClass}__modal__body__questions__title`}>
                                          {getTitle('5655', 'Questions')}
                                      </div>
                                      <div className={`${baseClass}__modal__body__questions__list`}>
                                          {questions.map((question) => renderQuestionItem(question))}
                                      </div>
                                  </div>
                              )}
                          </div>
                      </div>
                      {anonReason && isAnonModalOpen && (
                          <div
                              role={'presentation'}
                              className={`${baseClass}__reasonModal`}
                              onClick={() => setIsAnonModalOpen(false)}
                          >
                              <div className={`${baseClass}__reasonModal__body`}>
                                  <HStack gap={'8px'}>
                                      <svg
                                          width="16"
                                          height="16"
                                          viewBox="0 0 16 16"
                                          fill="none"
                                          xmlns="http://www.w3.org/2000/svg"
                                      >
                                          <path
                                              fillRule="evenodd"
                                              clipRule="evenodd"
                                              d="M12.6975 2.24291C12.6066 1.47001 11.8807 0.935326 11.1156 1.07767L8.03018 1.6517C8.01264 1.65497 7.99465 1.65497 7.97711 1.6517L4.89169 1.07767C4.12658 0.935326 3.4007 1.47001 3.30977 2.24291L2.7201 7.25517H1.67031C1.33894 7.25517 1.07031 7.5238 1.07031 7.85517C1.07031 8.18654 1.33894 8.45517 1.67031 8.45517H3.25365H4.83698H8.00364H11.1703H12.7536H14.337C14.6683 8.45517 14.937 8.18654 14.937 7.85517C14.937 7.5238 14.6683 7.25517 14.337 7.25517H13.2872L12.6975 2.24291ZM4.278 9.41142C2.71211 9.41142 1.44271 10.6808 1.44271 12.2467C1.44271 13.8126 2.71211 15.082 4.278 15.082C5.76715 15.082 6.98815 13.934 7.10426 12.4747H8.90291C9.01902 13.934 10.24 15.082 11.7292 15.082C13.2951 15.082 14.5645 13.8126 14.5645 12.2467C14.5645 10.6808 13.2951 9.41142 11.7292 9.41142C10.5047 9.41142 9.4616 10.1876 9.06488 11.2747H6.9423C6.54557 10.1876 5.50243 9.41142 4.278 9.41142Z"
                                              fill="#19191A"
                                          />
                                      </svg>
                                      <Text color={'#0C0C0D'} fontWeight={600} fontSize={'16px'} mb={'8px'}>
                                          {getTitle('8612', 'Anonymity reason')}
                                      </Text>
                                  </HStack>
                                  <div className={`${baseClass}__reasonModal__body__textWrap`}>
                                      <div className={`${baseClass}__reasonModal__body__textWrap__content`}>
                                          {anonReason}
                                      </div>
                                  </div>
                                  <button
                                      className={`${baseClass}__reasonModal__body__btn`}
                                      onClick={() => setIsAnonModalOpen(false)}
                                  >
                                      {getTitle('181', 'Close')}
                                  </button>
                              </div>
                          </div>
                      )}
                  </div>,
                  document.body
              )
            : null

    if (!status) {
        return (
            <div className={baseClass}>
                <div className={`${baseClass}__title__empty`}>{title}</div>
            </div>
        )
    }

    return (
        <div className={baseClass}>
            <div className={clsx(`${baseClass}__wrap`, `${baseClass}__${status.variant}`)}>
                <div className={`${baseClass}__title`}>{title}</div>

                <div className={`${baseClass}__info`}>
                    <div className={`${baseClass}__info--applied`}>
                        <div className={`${baseClass}__status ${baseClass}__status--${status.statusClass}`}>
                            {status.icon}
                            <span>{status.statusLabel}</span>
                        </div>
                        <div className={`${baseClass}__info--applied--row`}>
                            <span className={`${baseClass}__info--applied-string`}>
                                {getTitle('12989', 'You applied for this job')}
                            </span>{' '}
                            <span className={`${baseClass}__info--applied-date`}>({status.dateText || '—'})</span>
                        </div>
                    </div>

                    <div className={`${baseClass}__info--nav`}>
                        <ApplySkeleton
                            isLoading={isLoading || isDeleteApplicationLoading}
                            style={{ width: '100%', minHeight: '36px', minWidth: '120px' }}
                        >
                            <button
                                type="button"
                                className={`${baseClass}__info--nav--btn ${baseClass}__info--nav--btn--view`}
                                onClick={openModal}
                            >
                                {getTitle('10171', 'View details')}
                            </button>
                            {status.hasWithdrawButton && (
                                <button
                                    type="button"
                                    className={`${baseClass}__info--nav--btn ${baseClass}__info--nav--btn--withdraw`}
                                    onClick={() =>
                                        // deleteApplicationHandler(postApplySettingsData?.data?.application?.id)
                                        setIsWithdrawModalOpen(true)
                                    }
                                    disabled={isDeleteApplicationLoading}
                                >
                                    {getTitle('4942', 'Withdraw application')}
                                </button>
                            )}
                        </ApplySkeleton>
                    </div>
                </div>
            </div>
            <WithdrawApplicationModal
                isOpen={isWithdrawModalOpen}
                onClose={() => {
                    setIsWithdrawModalOpen(false)
                    resetWithdrawErrors()
                }}
                onWithdraw={(reason) => {
                    deleteApplicationHandler(postApplySettingsData?.data?.application?.id, reason)
                }}
                isLoading={isDeleteApplicationLoading}
                reasonError={withdrawReasonError}
                generalError={withdrawGeneralError}
                onResetErrors={resetWithdrawErrors}
            />
            {modal}
        </div>
    )
}

export default PostPageHeader2ApplyStatus
